// src/lib/actionsManager.js
import store from '~/store'
import { selectKeyBindings } from '~/store/keyBindings/selectors'
import { selectProjectionMap } from '~/store/outputMapping/selectors'
import { setUIProperty } from '~/store/ui'
import actions from './actions'
import { isMac } from './platform'

const logger = console.scope('actionsManager')

// Mapping Windows/Linux modifiers to Mac
export const keyFormatMap = {
	Ctrl: isMac ? '⌘' : 'Ctrl', // Command on Mac
	Alt: isMac ? '⌥' : 'Alt', // Option on Mac
	Shift: isMac ? '⇧' : 'Shift',
	Meta: isMac ? '⌘' : 'Win',
	Backslash: '\\',
	Backquote: '`',
	Up: isMac ? '↑' : 'Up',
	Down: isMac ? '↓' : 'Down',
	Left: isMac ? '←' : 'Left',
	Right: isMac ? '→' : 'Right',
	Delete: isMac ? '⌫' : 'Del',
	Backspace: isMac ? '⌫' : 'Backspace',
}

// Cache for stringified key objects
const keyObjectCache = new Map()

// Create a key object with platform-specific modifiers
const createKeyObject = (code, ctrlKey, altKey, shiftKey, metaKey, modKey) => {
	// On Mac, Cmd (metaKey) is used where Ctrl is used on Windows/Linux
	const useCtrl = isMac ? metaKey : ctrlKey

	if (modKey === code) modKey = false

	const cacheKey = `${code}:${useCtrl}:${altKey}:${shiftKey}:${modKey}`
	if (keyObjectCache.has(cacheKey)) {
		return keyObjectCache.get(cacheKey)
	}

	const keyObj = JSON.stringify({
		key: code,
		ctrlKey: useCtrl,
		altKey,
		shiftKey,
		metaKey,
		modKey,
	})

	keyObjectCache.set(cacheKey, keyObj)
	return keyObj
}

// Cache of normalized key names
const keyNameCache = new Map()

const normalKeys = {
	Del: 'Delete',
	'[': 'BracketLeft',
	']': 'BracketRight',
	';': 'Semicolon',
	"'": 'Quote',
	',': 'Comma',
	'.': 'Period',
	'/': 'Slash',
	'\\': 'Backslash',
	'=': 'Equal',
	'`': 'Backquote',
}

// Normalize key names for consistency across platforms
export const normalizeKeyName = key => {
	if (keyNameCache.has(key)) {
		return keyNameCache.get(key)
	}

	let normalized = key

	// On Mac, map both Delete and Backspace to Backspace
	if (isMac && (key === 'Delete' || key === 'Backspace')) {
		normalized = 'Backspace'
	} else if (/^\d$/.test(key)) {
		normalized = `Digit${key}`
	} else if (/^[A-Z]$/.test(key)) {
		normalized = `Key${key.toUpperCase()}`
	} else if (/^(Up|Down|Left|Right)$/.test(key)) {
		normalized = `Arrow${key}`
	} else if (key in normalKeys) {
		normalized = normalKeys[key]
	}

	keyNameCache.set(key, normalized)
	return normalized
}

// Format hotkey for display
export const formatHotkey = hotkey => {
	if (!hotkey) return ''

	return hotkey
		.split('+')
		.map(key => {
			if (key in keyFormatMap) {
				return keyFormatMap[key]
			}
			return key
		})
		.join(isMac ? ' ' : ' + ')
}

// Map of current bindings
let bindingsMap = new Map()

// Keep track of last bindings state
let lastBindingsState = null

// Create a reverse mapping of hotkeys to actions
const createBindingsMap = bindings => {
	const map = new Map()

	Object.entries(bindings).forEach(([actionName, hotkey]) => {
		if (!hotkey) return

		try {
			const [base, ...modifiers] = hotkey
				.split('+')
				.reverse()
				.map(s => s.trim())
			const key = normalizeKeyName(base)

			// On Mac, map Ctrl to Cmd
			const useCtrl = modifiers.includes('Ctrl')
			const useAlt = modifiers.includes('Alt')
			const useShift = modifiers.includes('Shift')
			let modKey = modifiers.find(m => /^[A-Z]$/.test(m))
			modKey = modKey ? normalizeKeyName(modKey) : false
			const keyObj = createKeyObject(key, useCtrl, useAlt, useShift, useCtrl && isMac, modKey)
			map.set(keyObj, actionName)
		} catch (error) {
			logger.error(`Invalid hotkey format for action ${actionName}:`, error)
		}
	})
	return map
}

// Update bindings map when store changes
let unsubscribe = null

const setupStoreSubscription = () => {
	if (unsubscribe) {
		unsubscribe()
	}

	unsubscribe = store.subscribe(() => {
		const currentBindings = selectKeyBindings(store.getState())

		if (JSON.stringify(currentBindings) !== lastBindingsState) {
			lastBindingsState = JSON.stringify(currentBindings)
			bindingsMap = createBindingsMap(currentBindings)
		}
	})
}

// Initialize bindings map
const initializeBindings = () => {
	const initialBindings = selectKeyBindings(store.getState())
	lastBindingsState = JSON.stringify(initialBindings)
	bindingsMap = createBindingsMap(initialBindings)
	setupStoreSubscription()
}

initializeBindings()

// Cleanup function
export const cleanup = () => {
	if (unsubscribe) {
		unsubscribe()
	}
	keyObjectCache.clear()
	keyNameCache.clear()
	bindingsMap.clear()
}

// Punctuation codes back to the glyph the defaults are written in (`[`, not `BracketLeft`); `normalizeKeyName` reads either.
const codeGlyph = Object.fromEntries(
	Object.entries(normalKeys)
		.filter(([k]) => k.length === 1)
		.map(([k, v]) => [v, k])
)
const simplifyCode = c => codeGlyph[c] ?? c.replace(/^(Key|Digit|Arrow)/, '')

/**
 * A keydown as a stored hotkey string (`Ctrl+Shift+K`), the format `key_bindings` holds — or null for a bare modifier. Ctrl is Cmd on Mac, as `hotkeyHandler` reads it. Keyboard learn binds through this.
 */
export const hotkeyFromEvent = e => {
	if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) return null
	const parts = []
	if (isMac ? e.metaKey : e.ctrlKey) parts.push('Ctrl')
	if (e.altKey) parts.push('Alt')
	if (e.shiftKey) parts.push('Shift')
	parts.push(simplifyCode(e.code))
	return parts.join('+')
}

const MAPPING_MODE_ACTIONS = new Set(['toggle_projection_map', 'fullscreen'])

/**
 * May an INPUT device (a hotkey, a MIDI control) fire `name` now? Every external input asks this before `triggerAction`; a button in the UI does not, because it can only be clicked where it is showing.
 *
 * Mapping mode covers the app, so an input firing there would act on what nobody can see.
 */
export const canTrigger = name => !selectProjectionMap(store.getState()) || MAPPING_MODE_ACTIONS.has(name)

// Handler for ignored keys
const allowedInInput = ['BracketLeft', 'BracketRight', 'Slash', 'Backslash', 'Quote', 'Semicolon', 'Enter']
const shouldIgnoreKey = e => {
	// While editing text inline, block all shortcuts without Ctrl/Alt modifiers
	if (window.editing_text_id) {
		return !(e.ctrlKey || e.altKey || e.metaKey)
	}
	const t = e.target.tagName
	if ((t === 'INPUT' && e.target.type !== 'range') || t === 'TEXTAREA') {
		return !allowedInInput.includes(e.code)
	}
	if (e.ctrlKey || e.altKey) return false
	if (t === 'BUTTON') return ['Enter', 'Space'].includes(e.code)
	return false
}

export const triggerAction = (action, payload) => {
	const entry = typeof action === 'string' ? actions[action] : action
	if (!entry) {
		logger.warn(`No action found for ${action}`)
		return false
	}

	if (entry.disabled) {
		logger.warn(`Disabled action: ${action}`)
		return false
	}
	// Gates both paths: a Redux `action` entry with a `condition` would otherwise fire regardless.
	if (entry.condition && !entry.condition()) return false

	let a = entry.action
	if (a) {
		if (typeof a === 'string') a = { type: a, payload }
		else if (a.constructor === Function) {
			a = a(payload)
		} else if (!a.type) {
			logger.error('Action missing type')
			return false
		}
		store.dispatch(a)
		return true
	}
	if (entry.callback) {
		entry.callback()
		return true
	}
	return false
}

export const hotkeyHandler = e => {
	if (shouldIgnoreKey(e)) {
		return false
	}

	if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) {
		return false
	}

	// const s = store.getState() if (s.ui.modal) {
	// 	return }

	// Escape exits no-UI mode
	if (e.code === 'Escape' && !store.getState().ui.show.ui) {
		store.dispatch(setUIProperty(['show.ui', true]))
		e.preventDefault()
		return true
	}

	// Create cached key object with platform-specific modifiers
	const keyObj = createKeyObject(e.code, e.ctrlKey, e.altKey, e.shiftKey, e.metaKey, window._pressed)
	const actionName = bindingsMap.get(keyObj)
	if (!actionName || !canTrigger(actionName)) return false

	let payload
	const item = e.target.closest('[data-list-item], [data-grid-item]')
	if (item) {
		payload = { ...item.dataset }
		// Support both data-list-item and data-grid-item
		payload.listItem = item.dataset.listItem || item.dataset.gridItem
	}

	try {
		logger.info('hotkey', `${e.key} -> ${actionName}`)
		const triggered = triggerAction(actionName, payload)

		// Prevent default browser shortcuts
		if (e.ctrlKey || e.altKey || e.metaKey) {
			e.preventDefault()
		}

		return triggered
	} catch (error) {
		logger.error(`Error executing action ${actionName}:`, error)
		return false
	}
}

export default {
	canTrigger,
	triggerAction,
	hotkeyHandler,
	formatHotkey,
	normalizeKeyName,
	cleanup,
	keyFormatMap,
}
